import { useEffect, useState } from 'react'
import { INVITATION_STATUS } from '../../domain/domainCodes'
import { MailPlus } from 'lucide-react'
import Button from '../../components/Button'
import ExcelUploadFields from '../../components/ExcelUploadFields'
import { FormBody, FormCols } from '../../components/Form'
import DatePicker from '../../components/DatePicker'
import FormRow from '../../components/FormRow'
import Drawer from '../../components/Drawer'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { strings as S } from './strings'

const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The legacy select is keyed on the English name and offers no empty option. */
const ADMIN_OPTIONS = [
  { value: 'false', label: S.filter.no },
  { value: 'true', label: S.filter.yes },
]

const EXCEL_COLUMNS = [
  { name: 'Email', required: true },
  { name: 'Department', required: false },
]

/**
 * The default the legacy writes: thirty hours from now — which is tomorrow, and this
 * field takes a DAY.
 *
 * It used to carry the time too (`2026-08-31 18:50:14`), and that broke the field at both
 * ends: `DatePicker` reads `YYYY-MM-DD` and showed the box EMPTY for anything longer, and
 * the server dropped the value on the floor (see `asExpiry` in the service). A date is
 * what the control offers and what the invitee understands — the link is good until the
 * end of that day.
 */
function defaultExpiry() {
  const date = new Date(Date.now() + 30 * 60 * 60 * 1000)
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * `#add_modal` — the single-invitation form and the excel bulk form, which the
 * legacy toggles inside one side drawer, so the list stays visible behind it. Editing reuses the single form and its title.
 *
 * The `Status` select exists in the markup but its container is `display:none` and
 * nothing un-hides it, so it is not rendered; its value still travels in the payload.
 */
/**
 * @param {{value: string, label: string}[]} groups  from the page payload. The value is the
 *        group's ENGLISH name, which is what `Invitations.GroupName` holds.
 */
/** Today as ISO `YYYY-MM-DD`, in LOCAL time — `toISOString()` would shift the day. */
function today() {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 10)
}

export default function InvitationFormModal({ open, mode, invitation, groups = [], onClose, onSubmit }) {
  const [values, setValues] = useState({})
  const [error, setError] = useState('')
  const [file, setFile] = useState(null)
  const [bulkExpiry, setBulkExpiry] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    setFile(null)
    setBulkExpiry(defaultExpiry())
    setValues(
      invitation
        ? {
            id: invitation.id,
            email: invitation.email ?? '',
            groupName: invitation.groupName ?? '',
            /* The stored value is a full timestamp; the picker takes the day out of it. */
            expiryDate: (invitation.expiryDate ?? '').slice(0, 10),
            isAdmin: invitation.isAdmin ? 'true' : 'false',
            status: invitation.status,
          }
        : { email: '', groupName: '', expiryDate: defaultExpiry(), isAdmin: 'false', status: INVITATION_STATUS.SENT },
    )
  }, [open, invitation])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submitSingle = (event) => {
    event.preventDefault()
    if (!values.email?.trim()) return setError(S.form.emailRequired)
    if (!EMAIL_RULE.test(values.email)) return setError(S.form.emailInvalid)
    setError('')
    return onSubmit(values)
  }

  const submitBulk = (event) => {
    event.preventDefault()
    if (!file) return setError(S.form.fileRequired)
    setError('')
    return onSubmit({ excel: file, expiryDate: bulkExpiry })
  }

  const bulk = mode === 'bulk'

  return (
    <Drawer
      dialog
      open={open}
      title={bulk ? S.form.bulkTitle : S.form.singleTitle}
      icon={MailPlus}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form={bulk ? 'invitation-bulk-form' : 'invitation-form'}>
            {bulk ? S.form.send : S.form.save}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {S.close}
          </Button>
        </>
      }
    >
      {bulk ? (
        <form id="invitation-bulk-form" onSubmit={submitBulk} noValidate>
          <FormBody>
            <ExcelUploadFields
              id="invitation-excel"
              name="excel"
              accept=".xls,.xlsx,.csv"
              columns={EXCEL_COLUMNS}
              templateHref="/root/ExcelTemplates/Invitations.xlsx"
              error={error}
              strings={S.excel}
              onChange={(selected) => {
                setFile(selected)
                setError('')
              }}
            />

            <FormRow label={S.form.expiry}>
              {(id) => (
                <DatePicker id={id} value={bulkExpiry} onChange={setBulkExpiry} min={today()} />
              )}
            </FormRow>
          </FormBody>
        </form>
      ) : (
        <form id="invitation-form" onSubmit={submitSingle} noValidate>
          <FormBody>
            {/* The email placeholder repeated the label, and the label's own
                trailing colon put a lone `:` at the wrong edge of an RTL
                line — both are gone. */}
            <FormRow label={S.form.email} error={error}>
              {(id) => (
                <TextField
                  id={id}
                  dir="ltr"
                  value={values.email ?? ''}
                  onChange={set('email')}
                  invalid={!!error}
                />
              )}
            </FormRow>

            <FormCols>
              <FormRow label={S.form.group}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.groupName ?? ''}
                    onChange={set('groupName')}
                    options={groups}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.isAdmin}>
                {(id) => (
                  <Select
                    id={id}
                    value={values.isAdmin ?? 'false'}
                    onChange={set('isAdmin')}
                    options={ADMIN_OPTIONS}
                  />
                )}
              </FormRow>
            </FormCols>

            <FormRow label={S.form.expiry}>
              {(id) => (
                <DatePicker
                  id={id}
                  value={values.expiryDate ?? ''}
                  onChange={set('expiryDate')}
                  /* An invitation that expired before it was sent is not useful. */
                  min={today()}
                />
              )}
            </FormRow>
          </FormBody>
        </form>
      )}
    </Drawer>
  )
}
